import { type KeyboardEvent, type MouseEvent } from 'react';
import { BookImage, Folder as FolderIcon } from 'lucide-react';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { useGalleryChildDropTarget } from '@/hooks/dragDrop/useGalleryChildDropTarget';
import { IndeterminateCheckbox } from '@/components/data-table/data-table-checkbox';
import { ChildNodeMenuItems } from '@/components/browser/child-node-menu-items';
import { type ChildTreeNode, toSelectedNode } from '@/lib/child-node';
import { TruncatedText } from '@/components/ui/truncated-text';
import { TableRow } from '@/components/ui/table';
import { ColumnType } from '@/types/column';
import { DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';

/** The asset table's Name column id, which a child's name has to line up under. */
const NAME_COLUMN_ID = 'name';

/**
 * List view's child folder/album (DM-2517).
 *
 * The name has to land in the *same* column as the asset names below it, so the row emits one cell
 * per visible column rather than spanning: empty cells for the columns around it, the folder/album
 * thumbnail in the thumbnail column, and the bare name in the Name cell. Because the table is
 * `table-fixed`, those cells take exactly the asset rows' column widths, so the name's left edge
 * matches by construction rather than by a hand-tuned offset that column visibility changes would
 * break.
 *
 * The name cell deliberately does NOT span the columns after Name. Spanning gave the name far more
 * room than an asset gets, so a child's name never truncated where an asset's did, and it stretched
 * the tooltip's hit area across every trailing column — hovering blank space three columns away
 * popped a tooltip for the folder.
 *
 * The thumbnail cell reuses trash's folder/album treatment (see pages/trash.tsx's Thumbnail column):
 * a full-bleed cell tinted with the node's background colour and a centred glyph, rather than a
 * small inline icon, so a folder row reads the same in both places.
 *
 * The select cell gets a real checkbox — even though a child is single-selected, never
 * multi-selected — because the inspector shows whichever folder/album is selected the same way it
 * shows a selected asset, and trash's list view puts the identical checkbox on its folder/album
 * rows. Leaving that column blank here would make list view seem to disagree with both about
 * whether a child row is a selectable thing. The drag-handle cell stays empty: children aren't
 * draggable, so only the alignment that column implies carries over.
 */
export const GalleryChildRow = ({
  node,
  visibleColumnIds,
  selected,
  onClick,
  onDoubleClick,
  onContextMenu,
  parentPath,
}: {
  node: ChildTreeNode;
  /** Ids of the currently visible leaf columns, in render order. */
  visibleColumnIds: Array<string>;
  selected?: boolean;
  /**
   * Forwards the click event, unlike a plain callback — the selection bar's measure-and-defer
   * decision (gallery-layout.tsx's `handleChildRowClick`) reads `event.currentTarget` to find this
   * row's `data-row-id` and its scroll container, the same way an asset row's click does (DM-1894).
   */
  onClick?: (event?: MouseEvent<HTMLElement>) => void;
  onDoubleClick?: () => void;
  /** Right-click selects the row before its menu opens, matching an asset row. */
  onContextMenu?: () => void;
  /** Forwarded to the context menu's upload items — see `ChildNodeMenuItems`. */
  parentPath?: { id: string; namePath: Array<string> };
}) => {
  const selectedNode = toSelectedNode(node);
  const { setNodeRef, isOver } = useGalleryChildDropTarget(selectedNode);
  const isAlbum = selectedNode.type === DAMNodeType.Album;
  const Icon = isAlbum ? BookImage : FolderIcon;

  const columnCount = visibleColumnIds.length;
  const nameColumnIndex = visibleColumnIds.indexOf(NAME_COLUMN_ID);
  const thumbnailColumnIndex = visibleColumnIds.indexOf(ColumnType.Thumbnail);
  const selectColumnIndex = visibleColumnIds.indexOf(ColumnType.Select);

  /*
   * Trash's list-view shape: the tint fills the whole cell with the glyph centred in it.
   *
   * Filled by `absolute inset-0` against the cell's `relative`, which is how trash does it — not by
   * `h-full`. A percentage height on a child of a <td> has no definite height to resolve against, so
   * `h-full` collapsed the tint to the glyph's own height (24px in a 36px cell) and left an untinted
   * band above and below it.
   */
  const thumbnail = (
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center',
        isAlbum ? 'bg-album-background-color' : 'bg-folder-background-color',
      )}
    >
      <Icon className={cn('icon-6', isAlbum ? 'stroke-album-color' : 'stroke-highlight-base')} strokeWidth={1} />
    </div>
  );

  // Enter opens the child, Space selects it — the keyboard equivalents of double- and
  // single-click, which a <tr> gives you neither of on its own.
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onDoubleClick?.();
    }
    if (event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  /*
   * `w-fit max-w-full` keeps the tooltip trigger the width of the name itself rather than the whole
   * cell — TruncatedText's trigger is a block div, so a short name would otherwise arm a tooltip
   * across the cell's empty space — while max-w-full still lets a long one ellipsize at the column.
   */
  // text-text-base: the same theme-aware color the chip uses (node-chip.tsx) — without it this
  // name fell back to the table's own fixed text color and stayed black in dark theme.
  const name = <TruncatedText text={selectedNode.name} className="w-fit max-w-full font-bold text-text-base" />;

  /** The small inline glyph, for the layouts that have no thumbnail cell to put the tinted one in. */
  const inlineGlyph = (
    <Icon className={cn('size-5 shrink-0', isAlbum ? 'text-album-color' : 'text-folder-color')} strokeWidth={2} />
  );

  /*
   * stopPropagation on click, not on the checkbox's own change: a click bubbles to the row's
   * onClick, which calls the same toggle this checkbox calls via onChange — left alone, checking
   * the box would select the child and then, in the same event, the row handler would toggle it
   * straight back off.
   */
  const checkbox = (
    <IndeterminateCheckbox
      checked={selected ?? false}
      onChange={() => onClick?.()}
      onClick={(event) => event.stopPropagation()}
      className="mx-auto"
      data-cy={`node-list-row-${selectedNode.id}-select`}
    />
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {/*
          The shared TableRow, not a bare <tr>: it carries the row separator every asset row has
          (`border-b-ui-void-gap`) plus the hover and `data-[state=selected]` treatments. Hand-rolling
          the <tr> meant child rows had no separator at all, so a folder holding a dozen subfolders
          rendered their tinted thumbnail cells as one unbroken block of colour — the rule was never
          missing from the design, the rows just weren't using it.
        */}
        <TableRow
          ref={setNodeRef}
          data-cy={`node-list-row-${selectedNode.id}`}
          // Lets the selection bar's measure-and-defer decision find this row the same way it finds
          // an asset row — without it, `closest('[data-row-id]')` and the reveal-scroll's
          // `querySelector` both came up empty, so a child row never got double-click protection.
          data-row-id={selectedNode.id}
          data-state={selected ? 'selected' : undefined}
          /*
           * Focusable, so the row's own Enter/Space handlers are reachable — without this it was
           * mouse-only.
           *
           * Deliberately NOT `aria-selected`. `components/ui/table.tsx` sets no role, so these rows
           * sit in an implicit `role="table"`, where screen readers surface row selection only
           * inside a `grid`/`treegrid` — the attribute would have looked like an accessibility fix
           * while announcing nothing. The row's checkbox already conveys the state properly, which
           * is the reason the select cell carries a real one.
           */
          tabIndex={0}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          // Composed with the trigger's own handler by Radix's Slot, so the menu still opens.
          onContextMenu={onContextMenu}
          onKeyDown={handleKeyDown}
          className={cn(
            // `ring-ui-separator` (this app's usual gray focus ring) falls short of WCAG 2.2's 3:1
            // contrast minimum for a focus indicator on a row with no resting ring to carry a
            // bevel-to-separator delta instead. `ring-highlight-base` (the app's blue) passes
            // comfortably in both themes — see node-chip.tsx's tile chip for the matching fix and
            // the full contrast measurements.
            //
            // `outline-hidden` sets the `--tw-outline-style` custom property to `none`, which is
            // exactly what the isOver ring below reads (`outline`/`outline-2` don't set that
            // variable themselves, they only consume it) — without `outline-solid` re-asserting it,
            // the row's own focus-hiding utility silently cancels its own drag ring.
            'cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-highlight-base',
            // Colored per node type, matching node-chip.tsx's tile chip isOver fix.
            //
            // `outline`, not `ring` (box-shadow): Chrome and Firefox both clip a `<tr>`'s box-shadow
            // to roughly its first few `<td>`s — confirmed live by inspecting `getBoundingClientRect`
            // (full row width) against the actually painted pixels (ring visible only through the
            // checkbox/thumbnail/Name cells). `outline` paints as a single rect around the row's
            // border box regardless of the cells inside it, so it doesn't have this gap.
            isOver &&
              (isAlbum
                ? 'outline-solid outline-2 -outline-offset-2 outline-album-color'
                : 'outline-solid outline-2 -outline-offset-2 outline-folder-color'),
          )}
        >
          {nameColumnIndex === -1 ? (
            // Name hidden: nothing to align against, so one spanning cell with the glyph inline.
            <td colSpan={columnCount} className="px-4 py-2" data-cy={`node-list-row-${selectedNode.id}-name`}>
              <div className="flex items-center gap-x-2">
                {inlineGlyph}
                {name}
              </div>
            </td>
          ) : (
            visibleColumnIds.map((columnId, index) => {
              if (index === nameColumnIndex) {
                return (
                  // p-2 matches the asset name cell's own padding, so the two names share a left
                  // edge by construction. The cells already line up; without this the text does
                  // not, because a cell with no left padding starts its text 8px further left.
                  <td key={columnId} className="p-2" data-cy={`node-list-row-${selectedNode.id}-name`}>
                    {thumbnailColumnIndex === -1 ? (
                      // No thumbnail column to tint, so the glyph rides inline — a row with neither
                      // would give no sign at all that it is a folder rather than an asset.
                      <div className="flex items-center gap-x-2">
                        {inlineGlyph}
                        {name}
                      </div>
                    ) : (
                      name
                    )}
                  </td>
                );
              }
              if (index === thumbnailColumnIndex) {
                return (
                  <td key={columnId} className="relative p-0" data-cy={`node-list-row-${selectedNode.id}-icon`}>
                    {thumbnail}
                  </td>
                );
              }
              if (index === selectColumnIndex) {
                // No data-cy on the cell: the checkbox inside already carries `-select`, and two
                // elements answering one selector makes `cy.get(...).click()` fail outright.
                return (
                  <td key={columnId} className="p-0">
                    {checkbox}
                  </td>
                );
              }
              return <td key={columnId} className="p-0" />;
            })
          )}
        </TableRow>
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-48">
        <ChildNodeMenuItems node={node} parentPath={parentPath} />
      </ContextMenuContent>
    </ContextMenu>
  );
};
